import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { AppConfig, appConfigService } from '@app/config';
import { InputControlConfig } from '@ntap/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  ReplaySubject,
  startWith,
  Subject,
  switchMap,
  takeUntil
} from 'rxjs';

export type IntegrationSearchResult = { id: number; label: string };

@Component({
  selector: 'app-integration-search',
  styleUrls: ['./geonames-integration-input.component.css'],
  template: `
    <ng-container *ngIf="!inputConfig.readOnly; else elseBlock">
      <p-dropdown
        [formControl]="control"
        [disabled]="inputConfig.disabled"
        [placeholder]="placeholder"
        [options]="(searchResults$ | async) || []"
        (onFilter)="filter($event)"
        optionLabel="label"
        optionValue="id"
        [filter]="true"
        appendTo="body"
        [lazy]="true"
      >
        <ng-template let-item pTemplate="item">
          <div class="flex flex-column align-items-start">
            <p class="font-semibold">{{ item[displayLabel] }}</p>
            <p class="font-light">{{ item[subheaderLabel] }}</p>
          </div>
        </ng-template>
      </p-dropdown>
    </ng-container>
    <ng-template #elseBlock>
      <ng-container *ngIf="searchResults$ | async as searchResult">
        <span *ngIf="searchResult.length; else unavailableBlock">{{
          searchResult[0].label
        }}</span>
        <ng-template #unavailableBlock>
          <span *ngIf="control.value" class="font-italic text-color-secondary">
            {{ unavailableLabel }} ({{ control.value }})
          </span>
        </ng-template>
      </ng-container>
    </ng-template>
    <ntap-show-form-errors [control]="control"></ntap-show-form-errors>
    <a
      [href]="link"
      target="__blank"
      class="p-2"
      *ngIf="link$ | async as link"
      [pTooltip]="linkTooltip"
    >
      <span class="pi pi-external-link"></span>
    </a>
  `
})
export default class IntegrationSearchComponent implements OnInit, OnDestroy {
  @Input()
  public control: FormControl;
  @Input()
  public inputConfig: InputControlConfig;
  @Input()
  public searchType: 'geonames' | 'viaf';
  @Input()
  public displayLabel: string;
  @Input()
  public subheaderLabel: string;
  @Input()
  public placeholder: string;
  @Input()
  public linkTooltip: string;
  @Input()
  public linkBase: string;
  @Input()
  public unavailableLabel = 'Podatak trenutno nedostupan';
  @Output()
  public searchResult = new EventEmitter<IntegrationSearchResult[]>();

  private onDestroy$ = new Subject<void>();

  public link$: Observable<string | undefined>;

  public searchResults$: Observable<IntegrationSearchResult[]> =
    new BehaviorSubject([]);
  private input$ = new ReplaySubject<{
    query: string;
    id: string;
  }>(1);

  private httpClient: HttpClient = inject(HttpClient);
  private config: AppConfig = inject(appConfigService);

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnInit(): void {
    const initialValue = this.control.value;
    if (initialValue) {
      this.input$.next({ id: initialValue, query: '' });
    }

    this.searchResults$ = this.input$.pipe(
      distinctUntilChanged(
        (prev, curr) => prev.query === curr.query && prev.id === curr.id
      ),
      debounceTime(800),
      filter(
        (input) => !!input.id || (!!input.query && input.query.length >= 3)
      ),
      switchMap((input) => {
        return this.httpClient.get<IntegrationSearchResult[]>(
          `${this.config.apiHost}search/${this.searchType}`,
          {
            params: {
              q: input.query ?? '',
              id: input.id
            }
          }
        );
      }),
      map((resp) => resp)
    );

    this.searchResults$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((searchResults) => {
        this.searchResult.next(searchResults);
      });

    this.link$ = this.control.valueChanges.pipe(
      startWith(initialValue),
      map((id) => {
        if (!id) return undefined;
        return `${this.linkBase}/${id}`;
      })
    );
  }

  public filter(event: any) {
    this.input$.next({ query: event.filter, id: '' });
  }
}
